import * as yup from 'yup'
import { MinimumTerm, NewPaymentPlan, PaymentPeriod, PaymentPlan } from './types'
import { t } from 'translations'

export const TWO_YEAR_TERM: MinimumTerm = 'P2Y'
export const ONE_YEAR_TERM: MinimumTerm = 'P1Y'

export interface PricingPlanFormValues {
    minimumTerm: MinimumTerm
    renewAutomatically: boolean
    renewIntoMonthly: boolean
    allowYearlyInstallments: boolean
    allowMonthlyInstallments: boolean
    totalPriceYearly: number | null
    totalPriceMonthly: number | null
    initialPayment: number | null
    allowPerYearPayment: boolean
    pricePerYear: number | null
    allowUpfrontPayment: boolean
    priceUpfront: number | null
}

export const formatRenewal = (renewal?: PaymentPeriod, plan?: PaymentPlan): string => {
    if (!renewal) {
        return t('products.pricing.pricingPlans.renewal.off')
    }
    if (renewal === 'P1M') {
        return t('products.pricing.pricingPlans.renewal.intoMonthly')
    }
    if (plan && isTwoYearPaymentPlan(plan)) {
        return t('products.pricing.pricingPlans.renewal.intoOneYear')
    }
    return t('products.pricing.pricingPlans.renewal.intoSame')
}

export const isTwoYearPaymentPlan = (plan: PaymentPlan): boolean =>
    plan.paymentInterval === 'P2Y' || plan.minimumCommitmentDuration === TWO_YEAR_TERM

// On two-year plans billed yearly, totalPrice is the whole-term total; the
// per-year charge comes back as installment (initialPayment on the way in).
export const getYearlyPrice = (plan: PaymentPlan): number =>
    isTwoYearPaymentPlan(plan) && plan.paymentInterval === 'P1Y'
        ? plan.installment ?? plan.initialPayment ?? plan.totalPrice / 2
        : plan.totalPrice

export const toNewPaymentPlans = (formValues: PricingPlanFormValues, articleUuid: string): NewPaymentPlan[] => {
    const plans: NewPaymentPlan[] = []

    const getAutomaticRenewal = (paymentInterval: PaymentPeriod): PaymentPeriod | undefined => {
        if (!formValues.renewAutomatically) {
            return undefined
        }
        if (formValues.renewIntoMonthly) {
            return 'P1M'
        }
        return paymentInterval
    }

    if (formValues.minimumTerm === TWO_YEAR_TERM) {
        // Two-year plans only ever renew into a 1-year annual plan.
        const twoYearRenewal: PaymentPeriod | undefined = formValues.renewAutomatically ? 'P1Y' : undefined

        if (formValues.allowPerYearPayment && formValues.pricePerYear != null) {
            plans.push({
                articleUuid,
                paymentInterval: 'P1Y',
                totalPrice: formValues.pricePerYear * 2,
                initialPayment: formValues.pricePerYear,
                minimumCommitmentDuration: TWO_YEAR_TERM,
                automaticRenewal: twoYearRenewal,
                memberCount: 0,
            })
        }

        if (formValues.allowUpfrontPayment && formValues.priceUpfront != null) {
            plans.push({
                articleUuid,
                paymentInterval: 'P2Y',
                totalPrice: formValues.priceUpfront,
                minimumCommitmentDuration: TWO_YEAR_TERM,
                automaticRenewal: twoYearRenewal,
                memberCount: 0,
            })
        }

        return plans
    }

    if (formValues.allowYearlyInstallments && formValues.totalPriceYearly != null) {
        plans.push({
            articleUuid,
            paymentInterval: 'P1Y',
            totalPrice: formValues.totalPriceYearly,
            automaticRenewal: getAutomaticRenewal('P1Y'),
            memberCount: 0,
        })
    }

    if (formValues.allowMonthlyInstallments && formValues.totalPriceMonthly != null) {
        plans.push({
            articleUuid,
            paymentInterval: 'P1M',
            totalPrice: formValues.totalPriceMonthly,
            initialPayment: formValues.initialPayment ?? undefined,
            automaticRenewal: getAutomaticRenewal('P1M'),
            memberCount: 0,
        })
    }

    return plans
}

export const pricingPlanToFormValues = (paymentPlans: PaymentPlan[]) => {
    if (paymentPlans.length === 0) {
        return
    }

    const formValues: PricingPlanFormValues = {
        minimumTerm: ONE_YEAR_TERM,
        renewAutomatically: false,
        renewIntoMonthly: false,
        allowYearlyInstallments: false,
        allowMonthlyInstallments: false,
        totalPriceYearly: null,
        totalPriceMonthly: null,
        initialPayment: null,
        allowPerYearPayment: false,
        pricePerYear: null,
        allowUpfrontPayment: false,
        priceUpfront: null,
    }

    const readRenewal = (renewal?: PaymentPeriod) => {
        if (renewal) {
            formValues.renewAutomatically = true
            if (renewal === 'P1M') {
                formValues.renewIntoMonthly = true
            }
        }
    }

    if (paymentPlans.some(isTwoYearPaymentPlan)) {
        formValues.minimumTerm = TWO_YEAR_TERM

        const perYearPaymentPlan = paymentPlans.find((plan) => plan.paymentInterval === 'P1Y')
        const upfrontPaymentPlan = paymentPlans.find((plan) => plan.paymentInterval === 'P2Y')

        if (perYearPaymentPlan) {
            formValues.allowPerYearPayment = true
            formValues.pricePerYear = getYearlyPrice(perYearPaymentPlan) ?? null
            readRenewal(perYearPaymentPlan.automaticRenewal)
        }

        if (upfrontPaymentPlan) {
            formValues.allowUpfrontPayment = true
            formValues.priceUpfront = upfrontPaymentPlan.totalPrice ?? null
            readRenewal(upfrontPaymentPlan.automaticRenewal)
        }

        return formValues
    }

    const yearlyPaymentPlan = paymentPlans.find((plan) => plan.paymentInterval === 'P1Y')
    const monthlyPaymentPlan = paymentPlans.find((plan) => plan.paymentInterval === 'P1M')

    if (yearlyPaymentPlan) {
        formValues.allowYearlyInstallments = true
        formValues.totalPriceYearly = yearlyPaymentPlan?.totalPrice ?? null
        readRenewal(yearlyPaymentPlan.automaticRenewal)
    } else {
        formValues.allowYearlyInstallments = false
    }

    if (monthlyPaymentPlan) {
        formValues.allowMonthlyInstallments = true
        formValues.totalPriceMonthly = monthlyPaymentPlan?.totalPrice ?? null
        formValues.initialPayment = monthlyPaymentPlan?.initialPayment ?? null
        if (monthlyPaymentPlan.automaticRenewal) {
            formValues.renewAutomatically = true
        }
    }

    return formValues
}

export const pricingPlanFormSchema = yup.object().shape({
    minimumTerm: yup.string().oneOf([ONE_YEAR_TERM, TWO_YEAR_TERM]).required(),
    renewAutomatically: yup.boolean().required(),
    renewIntoMonthly: yup.boolean().required(),
    allowYearlyInstallments: yup.boolean().when(['minimumTerm', 'allowMonthlyInstallments'], {
        is: (minimumTerm: MinimumTerm, allowMonthlyInstallments: boolean) =>
            minimumTerm === ONE_YEAR_TERM && !allowMonthlyInstallments,
        then: yup.boolean().oneOf([true], () => t('products.pricing.pricingPlans.validation.choosePaymentPlan')),
        otherwise: yup.boolean().required(),
    }),
    totalPriceYearly: yup.number().when(['minimumTerm', 'allowYearlyInstallments'], {
        is: (minimumTerm: MinimumTerm, allowYearlyInstallments: boolean) =>
            minimumTerm === ONE_YEAR_TERM && allowYearlyInstallments,
        then: yup.number().typeError(() => t('products.pricing.pricingPlans.validation.enterPrice'))
            .required(() => t('products.pricing.pricingPlans.validation.enterPrice'))
            .min(0, () => t('products.pricing.pricingPlans.validation.totalPriceNegative')),
        otherwise: yup.number().nullable(),
    }),
    allowMonthlyInstallments: yup.boolean().required(),
    totalPriceMonthly: yup.number().when(['minimumTerm', 'allowMonthlyInstallments'], {
        is: (minimumTerm: MinimumTerm, allowMonthlyInstallments: boolean) =>
            minimumTerm === ONE_YEAR_TERM && allowMonthlyInstallments,
        then: yup.number().typeError(() => t('products.pricing.pricingPlans.validation.enterPrice'))
            .required(() => t('products.pricing.pricingPlans.validation.enterPrice'))
            .min(0, () => t('products.pricing.pricingPlans.validation.totalPriceNegative')),
        otherwise: yup.number().nullable(),
    }),
    initialPayment: yup.number().when(['minimumTerm', 'allowMonthlyInstallments'], {
        is: (minimumTerm: MinimumTerm, allowMonthlyInstallments: boolean) =>
            minimumTerm === ONE_YEAR_TERM && allowMonthlyInstallments,
        then: yup.number().typeError(() => t('products.pricing.pricingPlans.validation.enterPrice'))
            .required(() => t('products.pricing.pricingPlans.validation.enterPrice'))
            .min(0, () => t('products.pricing.pricingPlans.validation.initialPaymentNegative')),
        otherwise: yup.number().nullable(),
    }),
    allowPerYearPayment: yup.boolean().when(['minimumTerm', 'allowUpfrontPayment'], {
        is: (minimumTerm: MinimumTerm, allowUpfrontPayment: boolean) =>
            minimumTerm === TWO_YEAR_TERM && !allowUpfrontPayment,
        then: yup.boolean().oneOf([true], () => t('products.pricing.pricingPlans.validation.choosePaymentOption')),
        otherwise: yup.boolean().required(),
    }),
    pricePerYear: yup.number().when(['minimumTerm', 'allowPerYearPayment'], {
        is: (minimumTerm: MinimumTerm, allowPerYearPayment: boolean) =>
            minimumTerm === TWO_YEAR_TERM && allowPerYearPayment,
        then: yup.number().typeError(() => t('products.pricing.pricingPlans.validation.enterPrice'))
            .required(() => t('products.pricing.pricingPlans.validation.enterPrice'))
            .min(0, () => t('products.pricing.pricingPlans.validation.priceNegative')),
        otherwise: yup.number().nullable(),
    }),
    priceUpfront: yup.number().when(['minimumTerm', 'allowUpfrontPayment'], {
        is: (minimumTerm: MinimumTerm, allowUpfrontPayment: boolean) =>
            minimumTerm === TWO_YEAR_TERM && allowUpfrontPayment,
        then: yup.number().typeError(() => t('products.pricing.pricingPlans.validation.enterPrice'))
            .required(() => t('products.pricing.pricingPlans.validation.enterPrice'))
            .min(0, () => t('products.pricing.pricingPlans.validation.priceNegative')),
        otherwise: yup.number().nullable(),
    }),
})
